import i18n from "@/locales/i18n";
import { message } from '@koi-design/uxd-ui';
import { resolveModelNameApi, resolveSlangSpecificSceneApi } from '@/api/robot';

/** Model type query param for resolve_model_name */
export const RESOLVE_MODEL_TYPE = {
  EMBEDDING: 'Embedding',
  RERANKING: 'Reranking',
  INTENT_SFT: 'IntentSFTModel',
  ASR: 'ASR',
  TTS: 'TTS'
};
function normalizeResolveModelPayload(res) {
  if (res == null) return null;
  if (typeof res === 'string') {
    const modelName = res.trim();
    return modelName ? {
      model_name: modelName,
      id: null,
      display_name: modelName,
      timbre: undefined
    } : null;
  }
  if (typeof res !== 'object') return null;
  if ('code' in res && res.code !== '000000') return null;
  const payload = res.data != null && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
  const modelNameCandidates = ['model_name', 'resolved_model_name', 'resolved_name', 'new_model_name', 'target_model_name', 'value'];
  const modelName = modelNameCandidates.map(key => payload[key]).find(value => typeof value === 'string' && value.trim())?.trim();
  if (!modelName) return null;
  return {
    model_name: modelName,
    id: payload.id ?? null,
    display_name: typeof payload.display_name === 'string' && payload.display_name.trim() ? payload.display_name.trim() : modelName,
    timbre: Object.prototype.hasOwnProperty.call(payload, 'timbre') ? payload.timbre : undefined
  };
}
const RESOLVE_MODEL_NAME_CACHE_TTL_MS = 90_000;
const RESOLVE_MODEL_NAME_MAX_CACHE = 256;
const resolveModelNameInflight = new Map();
const resolveModelNameCache = new Map();
function resolveModelNameCacheKey(name, modelType, timbreId) {
  const mt = modelType == null || modelType === '' ? '' : String(modelType);
  const tid = mt === RESOLVE_MODEL_TYPE.TTS && timbreId != null && timbreId !== '' ? String(timbreId) : '';
  return `${mt}\0${tid}\0${name}`;
}
function pruneResolveModelNameCache(now = Date.now()) {
  if (resolveModelNameCache.size <= RESOLVE_MODEL_NAME_MAX_CACHE) return;
  for (const [k, v] of resolveModelNameCache) {
    if (v.exp <= now) resolveModelNameCache.delete(k);
  }
  if (resolveModelNameCache.size <= RESOLVE_MODEL_NAME_MAX_CACHE) return;
  const entries = [...resolveModelNameCache.entries()].sort((a, b) => a[1].exp - b[1].exp);
  const drop = resolveModelNameCache.size - RESOLVE_MODEL_NAME_MAX_CACHE;
  for (let i = 0; i < drop; i++) {
    resolveModelNameCache.delete(entries[i][0]);
  }
}

/**
 * @param {string} modelName
 * @param {string} [modelType] empty for LLM; otherwise a RESOLVE_MODEL_TYPE value
 * @param {string|number} [timbreId] local TTS timbre id
 * @returns {Promise<{model_name: string, id: *, display_name: string}|null>}
 *
 * Uses the same key for concurrent callers: one HTTP request; result cached ~90s (max 256 keys).
 * Failed requests are not cached.
 */
export async function fetchResolvedModel(modelName, modelType, timbreId) {
  if (modelName == null || modelName === '') return null;
  const name = String(modelName).trim();
  if (!name) return null;
  const key = resolveModelNameCacheKey(name, modelType, timbreId);
  const now = Date.now();
  const hit = resolveModelNameCache.get(key);
  if (hit && hit.exp > now) {
    return hit.val;
  }
  if (hit) resolveModelNameCache.delete(key);
  const pending = resolveModelNameInflight.get(key);
  if (pending) return pending;
  const params = {
    model_name: name
  };
  if (modelType) params.model_type = modelType;
  if (modelType === RESOLVE_MODEL_TYPE.TTS && timbreId != null && timbreId !== '') {
    params.timbre_id = timbreId;
  }
  const p = (async () => {
    try {
      const res = await resolveModelNameApi(params);
      const next = normalizeResolveModelPayload(res);
      if (next && modelType === RESOLVE_MODEL_TYPE.TTS) {
        next.timbre = next.timbre ?? null;
      }
      const modelChanged = Boolean(next && next.model_name !== name);
      const timbreChanged = Boolean(next?.timbre && next.timbre.id != null && String(next.timbre.id) !== String(timbreId));
      const result = !next || !modelChanged && !timbreChanged ? null : {
        ...next,
        model_changed: modelChanged,
        timbre_changed: timbreChanged
      };
      resolveModelNameCache.set(key, {
        val: result,
        exp: Date.now() + RESOLVE_MODEL_NAME_CACHE_TTL_MS
      });
      pruneResolveModelNameCache();
      return result;
    } catch {
      return null;
    } finally {
      resolveModelNameInflight.delete(key);
    }
  })();
  resolveModelNameInflight.set(key, p);
  return p;
}
async function fetchResolvedModelOption(modelName, modelType) {
  return fetchResolvedModel(modelName, modelType);
}
export async function fetchResolvedModelName(modelName, modelType) {
  const resolved = await fetchResolvedModel(modelName, modelType);
  return resolved?.model_name || null;
}
const VOICE_MODEL_FIELDS = {
  [RESOLVE_MODEL_TYPE.ASR]: {
    name: 'asr_model_name',
    id: 'model_id_asr',
    displayName: 'asr_display_name'
  },
  [RESOLVE_MODEL_TYPE.TTS]: {
    name: 'tts_model_name',
    id: 'model_id_tts',
    displayName: 'tts_display_name'
  }
};
function pickVoiceModelLabel(displayName, modelName) {
  const display = typeof displayName === 'string' ? displayName.trim() : '';
  if (display) return display;
  return typeof modelName === 'string' ? modelName.trim() : '';
}
function pickVoiceTimbreName(target) {
  if (!target || typeof target !== 'object') return '';
  const nested = target.tts_config?.timbre_name;
  if (typeof nested === 'string' && nested.trim()) return nested.trim();
  if (typeof target.timbre_name === 'string' && target.timbre_name.trim()) {
    return target.timbre_name.trim();
  }
  return '';
}
function clearVoiceTimbreFields(target) {
  if (!target || typeof target !== 'object') return;
  if ('timbre_id' in target) target.timbre_id = null;
  if ('timbre_name' in target) target.timbre_name = '';
  if ('timbre' in target) target.timbre = '';
  if ('language_id' in target) target.language_id = undefined;
  if (target.tts_config && typeof target.tts_config === 'object') {
    target.tts_config = {
      ...target.tts_config,
      timbre: '',
      timbre_code: '',
      timbre_name: '',
      language_id: undefined
    };
  }
}
export function applyVoiceTimbre(target, timbre) {
  if (!target || !timbre || typeof timbre !== 'object') return false;
  const changed = target.timbre_id == null || String(target.timbre_id) !== String(timbre.id);
  if (!changed) return false;
  target.timbre_id = timbre.id;
  if ('timbre_code' in target || !target.tts_config) {
    target.timbre_code = timbre.timbre_code || '';
  }
  if ('timbre_name' in target || !target.tts_config) {
    target.timbre_name = timbre.timbre_name || '';
  }
  if (target.tts_config && typeof target.tts_config === 'object') {
    target.tts_config = {
      ...target.tts_config,
      timbre: timbre.timbre_code || '',
      timbre_code: timbre.timbre_code || '',
      timbre_name: timbre.timbre_name || '',
      language_id: timbre.language_id
    };
  }
  if ('language_id' in target) target.language_id = timbre.language_id;
  if ('gender' in target) target.gender = timbre.gender;
  if ('language_code' in target) target.language_code = timbre.language_code;
  if ('language_name' in target) target.language_name = timbre.language_name;
  return true;
}

/**
 * Replaces a persisted ASR/TTS model only when resolve_model_name returns a
 * different model_name. Empty responses and request failures keep local data.
 * @returns {Promise<{modelType: string, fromLabel: string, toLabel: string, timbreName: string, modelChanged: boolean, timbreChanged: boolean, requiresTimbreSelection: boolean}|null>}
 */
export async function applyVoiceModelResolve(target, modelType) {
  const fields = VOICE_MODEL_FIELDS[modelType];
  if (!target || !fields) return null;
  const currentName = target[fields.name];
  if (!currentName) return null;
  const resolved = await fetchResolvedModel(currentName, modelType, modelType === RESOLVE_MODEL_TYPE.TTS ? target.timbre_id : undefined);
  if (!resolved) return null;
  const fromLabel = pickVoiceModelLabel(target[fields.displayName], currentName);
  const toLabel = pickVoiceModelLabel(resolved.display_name, resolved.model_name);
  const timbreName = modelType === RESOLVE_MODEL_TYPE.TTS ? pickVoiceTimbreName(target) : '';
  if (resolved.model_changed) {
    target[fields.name] = resolved.model_name;
    target[fields.id] = resolved.id;
    target[fields.displayName] = resolved.display_name;
  }
  if (modelType === RESOLVE_MODEL_TYPE.TTS && resolved.timbre) {
    applyVoiceTimbre(target, resolved.timbre);
  } else if (modelType === RESOLVE_MODEL_TYPE.TTS && resolved.model_changed && resolved.timbre === null) {
    clearVoiceTimbreFields(target);
  }
  return {
    modelType,
    fromLabel,
    toLabel,
    timbreName,
    toTimbreName: modelType === RESOLVE_MODEL_TYPE.TTS ? resolved.timbre?.timbre_name || '' : '',
    modelChanged: resolved.model_changed,
    timbreChanged: resolved.timbre_changed,
    requiresTimbreSelection: modelType === RESOLVE_MODEL_TYPE.TTS && resolved.model_changed && resolved.timbre === null
  };
}
export function notifyVoiceModelReplacements(replacements = []) {
  replacements.forEach(item => {
    if (!item?.fromLabel || !item?.toLabel) return;
    if (item.modelType === RESOLVE_MODEL_TYPE.ASR) {
      message.warning(i18n.global.t('libs.resolveModelReplacement.dda8b32219604245b4a935a483331a29', [item.fromLabel, item.toLabel]));
      return;
    }
    if (item.modelType === RESOLVE_MODEL_TYPE.TTS) {
      if (item.requiresTimbreSelection) {
        message.warning(i18n.global.t('libs.resolveModelReplacement.301a084dbb69c3689a8c3c1fe7e3947c', [item.fromLabel, item.toLabel]));
        return;
      }
      if (!item.modelChanged && item.timbreChanged) {
        message.warning(i18n.global.t('libs.resolveModelReplacement.06176e074e04ad99a2bf4e642683f74a', [item.timbreName || '', item.toTimbreName || '']));
        return;
      }
      if (!item.timbreChanged) {
        message.warning(i18n.global.t('libs.resolveModelReplacement.e71994335561fcc586e7eb94cd01a2b2', [item.fromLabel, item.toLabel]));
        return;
      }
      message.warning(i18n.global.t('libs.resolveModelReplacement.67cdb169109a14432490665bd7f412d1', [item.fromLabel, item.timbreName || '', item.toLabel, item.toTimbreName || '']));
    }
  });
}
export function getActiveVoiceModelReplacements(info, replacements = []) {
  const scopeByType = {
    0: 'companion',
    1: 'webcall',
    2: 'copilot'
  };
  const activeScope = scopeByType[Number(info?.interaction_type)];
  if (activeScope) {
    return replacements.filter(item => item.scope === activeScope);
  }
  if (info?.realtime_config) {
    return replacements.filter(item => item.scope === 'realtime');
  }
  return replacements.filter(item => item.scope !== 'realtime');
}

/**
 * @param {object} info
 * @param {{ notify?: boolean, activeScopes?: string[] }} [options]
 * @returns {Promise<Array>}
 */
export async function applyRealtimeVoiceModelResolves(info, options = {}) {
  if (!info) return [];
  const {
    notify = false,
    activeScopes
  } = options;
  const configs = [[info.companion_voice, RESOLVE_MODEL_TYPE.ASR, 'companion'], [info.companion_voice, RESOLVE_MODEL_TYPE.TTS, 'companion'], [info.webcall, RESOLVE_MODEL_TYPE.ASR, 'webcall'], [info.webcall, RESOLVE_MODEL_TYPE.TTS, 'webcall'], [info.copilot_config, RESOLVE_MODEL_TYPE.ASR, 'copilot'], [info.realtime_config, RESOLVE_MODEL_TYPE.TTS, 'realtime']];
  const results = await Promise.all(configs.map(async ([target, modelType, scope]) => {
    const detail = await applyVoiceModelResolve(target, modelType);
    return detail ? {
      ...detail,
      scope
    } : null;
  }));
  const replacements = results.filter(Boolean);
  if (notify) {
    const visible = Array.isArray(activeScopes) ? replacements.filter(item => activeScopes.includes(item.scope)) : getActiveVoiceModelReplacements(info, replacements);
    notifyVoiceModelReplacements(visible);
  }
  return replacements;
}

/**
 * Resolve the currently selected ASR/TTS option. Prefer matching by resolved id,
 * then by model_name. Returns the option that should stay selected, or null when
 * unchanged / unresolved.
 */
export async function resolveVoiceModelSelectOption(selectedOption, modelType, options = [], timbreId) {
  if (!selectedOption?.model_name) return null;
  const resolved = await fetchResolvedModel(selectedOption.model_name, modelType, timbreId);
  if (!resolved) return null;
  const byId = options.find(item => String(item.value) === String(resolved.id));
  if (byId) return byId;
  const byName = options.find(item => item.model_name === resolved.model_name);
  if (byName) return byName;
  return {
    value: resolved.id,
    label: resolved.display_name || resolved.model_name,
    model_name: resolved.model_name
  };
}
export async function resolveVoiceModelSelection(selectedOption, modelType, options = [], timbreId) {
  if (!selectedOption?.model_name) return null;
  const resolved = await fetchResolvedModel(selectedOption.model_name, modelType, timbreId);
  if (!resolved) return null;
  const option = options.find(item => String(item.value) === String(resolved.id)) || options.find(item => item.model_name === resolved.model_name) || {
    value: resolved.id,
    label: resolved.display_name || resolved.model_name,
    model_name: resolved.model_name
  };
  return {
    option,
    resolved
  };
}
function normalizeSlangIdPayload(res) {
  if (res == null) return null;
  if (typeof res === 'number' && !Number.isNaN(res)) return res;
  if (typeof res === 'boolean') return null;
  if (typeof res === 'string') {
    const t = res.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isNaN(n) && String(n) === t) return n;
    return t;
  }
  if (typeof res !== 'object') return null;
  if ('code' in res && String(res.code) !== '000000') return null;
  const payload = res.data != null && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;

  // VoiceSlangDialog / get_slang_scenes use item.id as option value; resolve API often returns
  // full scene row with both `id` (list PK) and `scene_id` (logical). Prefer `id` so optionByKey matches.
  // Put `slang_id` last: backend may echo the request slang_id (stale PK) while `id` is the migrated row.
  const keys = ['resolved_slang_id', 'id', 'scene_id', 'value', 'slang_id'];
  for (const k of keys) {
    const v = payload[k];
    if (v != null && v !== '') return v;
  }
  return null;
}
export function coerceSlangIdQueryParam(slangId) {
  if (slangId == null || slangId === '') return null;
  const s = String(slangId).trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isNaN(n) && Number.isInteger(n)) return n;
  return slangId;
}
function unwrapSlangResolvePayload(res) {
  if (res == null || typeof res !== 'object') return null;
  if ('code' in res && String(res.code) !== '000000') return null;
  if (res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}
export function pickSceneListIdFromResolvePayload(payload, list) {
  if (!payload || typeof payload !== 'object' || !Array.isArray(list) || !list.length) {
    return null;
  }
  const pid = payload.id;
  if (pid == null || pid === '') return null;
  const hit = list.find(item => String(item.id) === String(pid));
  return hit ? hit.id : null;
}
export async function resolveSlangIdToSceneListItemId(slangId, list) {
  const param = coerceSlangIdQueryParam(slangId);
  if (param == null || !Array.isArray(list) || !list.length) return null;
  try {
    const res = await resolveSlangSpecificSceneApi({
      slang_id: param
    });
    const payload = unwrapSlangResolvePayload(res);
    if (!payload || typeof payload !== 'object') return null;
    const nextId = pickSceneListIdFromResolvePayload(payload, list);
    if (nextId == null) return null;
    // Always return list row id when resolve `data.id` matches the scene list (same as get_slang_scenes item.id).
    // Do not compare to request param: we need to re-assign option value for u-select even when types differ.
    return nextId;
  } catch {
    return null;
  }
}

/**
 * @returns {Promise<*|null>} replacement slang id, or null if unchanged / error
 */
export async function fetchResolvedSlangId(slangId) {
  const param = coerceSlangIdQueryParam(slangId);
  if (param == null) return null;
  try {
    const res = await resolveSlangSpecificSceneApi({
      slang_id: param
    });
    const next = normalizeSlangIdPayload(res);
    if (next == null || String(next) === String(param)) return null;
    return next;
  } catch {
    return null;
  }
}

/** Global intent / flow config dialog on botInfo */
export async function applyGlobalIntentModelResolves(botInfo) {
  if (!botInfo) return;
  const b = botInfo;
  if (b.intent_final_llm_model_name) {
    const n = await fetchResolvedModelName(b.intent_final_llm_model_name, '');
    if (n) b.intent_final_llm_model_name = n;
  }
  if (b.intent_embedding_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_model_name, RESOLVE_MODEL_TYPE.EMBEDDING);
    if (n) b.intent_embedding_model_name = n;
  }
  if (b.intent_embedding_rerank_enable && b.intent_embedding_rerank_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_rerank_model_name, RESOLVE_MODEL_TYPE.RERANKING);
    if (n) b.intent_embedding_rerank_model_name = n;
  }
  if (b.intent_embedding_llm_enable && b.intent_embedding_llm_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_llm_model_name, '');
    if (n) b.intent_embedding_llm_model_name = n;
  }
  if (b.intent_sft_model_name) {
    const n = await fetchResolvedModelName(b.intent_sft_model_name, RESOLVE_MODEL_TYPE.INTENT_SFT);
    if (n) b.intent_sft_model_name = n;
  }
}

/** Vector general config dialog */
export async function applyVectorGeneralModelResolves(botInfo) {
  if (!botInfo) return;
  const b = botInfo;
  if (b.intent_embedding_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_model_name, RESOLVE_MODEL_TYPE.EMBEDDING);
    if (n) b.intent_embedding_model_name = n;
  }
  if (b.intent_embedding_rerank_enable && b.intent_embedding_rerank_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_rerank_model_name, RESOLVE_MODEL_TYPE.RERANKING);
    if (n) b.intent_embedding_rerank_model_name = n;
  }
  if (b.intent_embedding_llm_enable && b.intent_embedding_llm_model_name) {
    const n = await fetchResolvedModelName(b.intent_embedding_llm_model_name, '');
    if (n) b.intent_embedding_llm_model_name = n;
  }
}
export async function applyKnowledgeBaseRerankModelResolve(formModel, optionsRef) {
  if (!formModel) return false;
  const opts = optionsRef?.value ?? optionsRef;
  if (!Array.isArray(opts) || !opts.length) return false;
  const cur = String(formModel.rerank_model ?? '').trim();
  if (!cur) return false;
  const resolved = await fetchResolvedModelName(cur, RESOLVE_MODEL_TYPE.RERANKING);
  let next = null;
  if (resolved && opts.some(o => o.value === resolved)) {
    next = resolved;
  } else if (opts.some(o => o.value === cur)) {
    next = cur;
  }
  if (next == null || formModel.rerank_model === next) return false;
  formModel.rerank_model = next;
  return true;
}
export async function applyAgenticRerankModelResolve(agenticConfig, optionsRef) {
  if (!agenticConfig) return false;
  const options = optionsRef?.value ?? optionsRef;
  const current = String(agenticConfig.rerank_model_name ?? '').trim();
  if (!current || !Array.isArray(options) || !options.length) return false;
  if (isModelAvailable(current, options)) return false;
  const resolved = await fetchResolvedModelName(current, RESOLVE_MODEL_TYPE.RERANKING);
  const nextModel = isModelAvailable(resolved, options) ? resolved : options[0]?.value || options[0]?.model_name || '';
  if (!nextModel || nextModel === current) return false;
  agenticConfig.rerank_model_name = nextModel;
  return true;
}
export async function applyAgentEmbeddingModelResolve(botInfo, embeddingOptionsRef) {
  const current = String(botInfo?.emb_model ?? '').trim();
  if (!current || !hasModelOptions(embeddingOptionsRef)) return false;
  const resolved = await fetchResolvedModelOption(current, RESOLVE_MODEL_TYPE.EMBEDDING);
  if (!ensureResolvedModelOption(resolved, embeddingOptionsRef)) return false;
  botInfo.emb_model = resolved.model_name;
  return true;
}
export async function applyAiWriteEmbeddingModelResolve(formModel, embeddingOptionsRef) {
  const current = String(formModel?.emb_model ?? '').trim();
  if (!current || !hasModelOptions(embeddingOptionsRef) || isModelAvailable(current, embeddingOptionsRef)) {
    return false;
  }
  return applyAgentEmbeddingModelResolve(formModel, embeddingOptionsRef);
}
export async function applyAiWriteLlmModelResolve(formModel, llmOptionsRef) {
  const current = String(formModel?.llm_model ?? '').trim();
  if (!current || !hasModelOptions(llmOptionsRef) || isModelAvailable(current, llmOptionsRef)) {
    return false;
  }
  const resolved = await fetchResolvedModelOption(current, '');
  if (!resolved?.model_name || resolved.model_name === current) return false;
  const modelOption = findModelOption(resolved.model_name, llmOptionsRef) || ensureResolvedLlmModelOption(resolved, formModel, llmOptionsRef);
  if (!modelOption) return false;
  formModel.llm_model = modelOption.value;
  formModel.llm_model_name = modelOption.label;
  formModel.top_p = modelOption.top_p ?? 1;
  formModel.max_tokens = modelOption.default_tokens;
  formModel.support_function_call = modelOption.support_function_call;
  return true;
}
export async function applyDatabaseRerankModelResolves(formModel, rerankOptionsRef) {
  if (!formModel || !hasModelOptions(rerankOptionsRef)) return false;
  const options = rerankOptionsRef?.value ?? rerankOptionsRef;
  const fallbackModel = options[0]?.value || options[0]?.model_name || '';
  let changed = false;
  for (const field of ['db_rerank_model', 'qa_rerank_model']) {
    const current = String(formModel[field] ?? '').trim();
    if (!current) continue;
    if (isModelAvailable(current, rerankOptionsRef)) continue;
    const resolved = await fetchResolvedModelName(current, RESOLVE_MODEL_TYPE.RERANKING);
    const nextModel = isModelAvailable(resolved, rerankOptionsRef) ? resolved : fallbackModel;
    if (!nextModel || nextModel === current) continue;
    formModel[field] = nextModel;
    changed = true;
  }
  return changed;
}
export async function syncEmbeddingRerankModelWithResolve(formModel, rerankOptionsRef) {
  if (!formModel?.embedding_rerank_enable) return;
  const opts = rerankOptionsRef?.value ?? rerankOptionsRef;
  if (!Array.isArray(opts) || !opts.length) return;
  const cur = String(formModel.embedding_rerank_model_name ?? '').trim();
  const inList = cur && opts.some(o => o.value === cur);
  if (!inList) {
    formModel.embedding_rerank_model_name = opts.find(item => item.topping)?.value || opts[0]?.value || '';
  }
  const wrap = {
    rerank_model: formModel.embedding_rerank_model_name
  };
  const changed = await applyKnowledgeBaseRerankModelResolve(wrap, rerankOptionsRef);
  if (changed) {
    formModel.embedding_rerank_model_name = wrap.rerank_model;
  }
}
export async function applySemanticJudgmentModelResolves(formModel, rerankOptionsRef, llmOptionsRef, embeddingOptionsRef) {
  if (!formModel) return;
  const m = formModel;
  if (m.embedding_model_name) {
    const resolved = await fetchResolvedModelOption(m.embedding_model_name, RESOLVE_MODEL_TYPE.EMBEDDING);
    if (ensureResolvedModelOption(resolved, embeddingOptionsRef)) {
      m.embedding_model_name = resolved.model_name;
    }
  }
  if (m.embedding_llm_enable && m.embedding_llm_model_name) {
    const n = await fetchResolvedModelName(m.embedding_llm_model_name, '');
    if (isModelAvailable(n, llmOptionsRef)) {
      m.embedding_llm_model_name = n;
    }
  }
  if (m.intent_final_llm_model_name) {
    const n = await fetchResolvedModelName(m.intent_final_llm_model_name, '');
    if (isModelAvailable(n, llmOptionsRef)) {
      m.intent_final_llm_model_name = n;
    }
  }
  // Node global form has sft_model_name but no sft_model_enable (unlike per-intention edit)
  if (m.sft_model_name && String(m.sft_model_name).trim()) {
    const n = await fetchResolvedModelName(m.sft_model_name, RESOLVE_MODEL_TYPE.INTENT_SFT);
    if (n) m.sft_model_name = n;
  }
  if (rerankOptionsRef) {
    await syncEmbeddingRerankModelWithResolve(formModel, rerankOptionsRef);
  }
}
function isModelAvailable(modelName, optionsRef) {
  if (!modelName) return false;
  const options = optionsRef?.value ?? optionsRef;
  if (!Array.isArray(options) || !options.length) return false;
  return options.some(option => {
    if (Array.isArray(option?.children)) {
      return isModelAvailable(modelName, option.children);
    }
    return option?.value === modelName || option?.model_name === modelName;
  });
}
function findModelOption(modelName, optionsRef) {
  if (!modelName) return null;
  const options = optionsRef?.value ?? optionsRef;
  if (!Array.isArray(options) || !options.length) return null;
  for (const option of options) {
    if (Array.isArray(option?.children)) {
      const child = findModelOption(modelName, option.children);
      if (child) return child;
      continue;
    }
    if (option?.value === modelName || option?.model_name === modelName) {
      return option;
    }
  }
  return null;
}
function ensureResolvedLlmModelOption(resolved, formModel, optionsRef) {
  const options = optionsRef?.value ?? optionsRef;
  if (!Array.isArray(options)) return null;
  const configuredTokens = Number(formModel?.max_tokens);
  const defaultTokens = Number.isFinite(configuredTokens) ? configuredTokens : 100;
  const option = {
    label: resolved.display_name || resolved.model_name,
    value: resolved.model_name,
    support_function_call: Boolean(formModel?.support_function_call),
    support_multimodal: false,
    max_tokens: Math.max(defaultTokens, 100),
    min_tokens: 1,
    default_tokens: defaultTokens,
    model_feature: '',
    top_p: formModel?.top_p ?? 1,
    prompt_list: []
  };
  if (resolved.id != null) option.id = resolved.id;
  const group = {
    label: option.label,
    children: [option]
  };
  if (optionsRef && !Array.isArray(optionsRef) && 'value' in optionsRef) {
    optionsRef.value = [...options, group];
  } else {
    options.push(group);
  }
  return option;
}
export function preserveSelectedModelOption(nextOptions, currentOptionsRef, selectedModelName) {
  const options = Array.isArray(nextOptions) ? nextOptions : [];
  const selected = String(selectedModelName ?? '').trim();
  if (!selected || isModelAvailable(selected, options)) return options;
  const currentOptions = currentOptionsRef?.value ?? currentOptionsRef;
  if (!Array.isArray(currentOptions)) return options;
  const selectedOption = currentOptions.find(item => item?.model_name === selected || item?.value === selected);
  return selectedOption ? [...options, selectedOption] : options;
}
function ensureResolvedModelOption(resolved, optionsRef) {
  if (!resolved?.model_name) return false;
  if (isModelAvailable(resolved.model_name, optionsRef)) return true;
  const options = optionsRef?.value ?? optionsRef;
  if (!Array.isArray(options)) return false;
  const option = {
    model_name: resolved.model_name,
    display_name: resolved.display_name || resolved.model_name
  };
  if (resolved.id != null) option.id = resolved.id;
  if (optionsRef && !Array.isArray(optionsRef) && 'value' in optionsRef) {
    optionsRef.value = [...options, option];
  } else {
    options.push(option);
  }
  return true;
}
function hasModelOptions(optionsRef) {
  const options = optionsRef?.value ?? optionsRef;
  return Array.isArray(options) && options.length > 0;
}